import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import Header2 from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import {
  faArrowLeft,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../Components/Page/Page.css";
import "./ContactManagement.css";
import "./ContactManagementView.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const PROFESSION_OPTIONS = [
  "Student",
  "Scientist",
  "Engineer",
  "Manufacturer",
  "Academic",
  "Government",
  "Defense",
  "Marketing",
  "Law",
  "Health",
  "Other",
];

const INTEREST_OPTIONS = [
  "Engines",
  "Software",
  "Research",
  "Manufacturing",
  "Public Policy",
  "Climate",
  "Economics",
  "Health",
  "Other",
];

interface Contact {
  cidn: number;
  ipaddress: string;
  firstname: string;
  lastname: string;
  middlename?: string;
  contacttype?: string;
  profession: string[];
  profession_other?: string;
  interests: string[];
  interests_other?: string;
  valid: boolean;
  email: string;
  companyname?: string;
  title?: string;
  entitytype?: string;
  dateadded: string;
  dateupdated: string;
  hasuser: boolean;
  streetaddress: string;
  streetaddress2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phonenumber: string;
  datasource: string;
}

interface ContactAddress {
  contactaddressid: number;
  streetaddress?: string;
  streetaddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  primaryind: boolean;
}

interface ContactEmail {
  contactemailid: number;
  email?: string;
  primaryind: boolean;
}

interface ContactPhone {
  contactphoneid: number;
  phonenumber?: string;
  countrycode?: string;
  phoneext?: string;
  primaryind: boolean;
}

interface ContactCompany {
  contactcompanyid: number;
  companyname?: string;
  entityid?: number;
  entitytype?: string;
  title?: string;
  primaryind: boolean;
}

interface Users {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  cidn: number;
  cidnprimaryind: boolean;
}

// Define props for UserOption
interface UserOption {
  id: string;
  firstlastemail: string;
  cidn: number;
}

// Define props for States
interface StateOption {
  stateprovinceabbrv: string;
  stateprovincename: string;
}

interface ContactExists {
  cidn: number;
  firstname: string;
  lastname: string;
  email: string;
  activeind: boolean;
}

interface ContactCRMInfo {
  crm_id: number;
  owner: string;
  owner_cidn: number;
  title: string;
  organization: string;
  pipeline: string;
  stage: string;
  last_stage_change: string;
  status: string;
  date_added: string;
  date_update: string;
}

export default function ContactManagement() {
  const { cidn } = useParams();
  const location = useLocation();
  const [contact, setContact] = useState<Contact | null>(null);
  const [contactexists, setContactExists] = useState<ContactExists | null>(
    null,
  );
  const [emailError, setEmailError] = useState("");

  //Load logged in User
  const { user, setUser } = useUser();
  useEffect(() => {
    // 1. Always initialize with whatever user context is currently available
    if (user) {
    }
  }, [user?.id]); // runs when user is ready

  //Editing of Contact
  const [isEditing, setIsEditing] = useState(false);
  const [editedContact, setEditedContact] = useState(contact);

  useEffect(() => {
    setEditedContact(contact);
  }, [contact]);

  //Editing of Users Associated
  const [users, setUsers] = useState<Users[]>([]);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [userdropdown, setUserDropDown] = useState<UserOption[]>([]);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editedUser, setEditedUser] = useState<Users | null>(null);
  const [newUser, setNewUser] = useState<Users>({
    id: "",
    firstname: "",
    lastname: "",
    email: "",
    cidn: 0,
    cidnprimaryind: false,
  });

  //Editing of Address
  const [contactaddress, setContactAddress] = useState<ContactAddress[]>([]);
  const [newAddress, setNewAddress] = useState<ContactAddress>({
    contactaddressid: 0,
    streetaddress: "",
    streetaddress2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    primaryind: false,
  });
  const [states, setStates] = useState<StateOption[]>([]);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [editedAddress, setEditedAddress] = useState<ContactAddress | null>(
    null,
  );

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStateProvince();
        setStates(response);
      } catch (error) {
        console.error("Error loading states:", error);
      }
    };
    fetchStates();
  }, []);

  //Editing of Email
  const [contactemail, setContactEmail] = useState<ContactEmail[]>([]);
  const [newEmail, setNewEmail] = useState<ContactEmail>({
    contactemailid: 0,
    email: "",
    primaryind: false,
  });
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [editingEmailId, setEditingEmailId] = useState<number | null>(null);
  const [editedEmail, setEditedEmail] = useState<ContactEmail | null>(null);

  //Editing of Phone
  const [contactphone, setContactPhone] = useState<ContactPhone[]>([]);
  const [isAddingPhone, setIsAddingPhone] = useState(false);
  const [editingPhoneId, setEditingPhoneId] = useState<number | null>(null);
  const [editedPhone, setEditedPhone] = useState<ContactPhone | null>(null);

  const [newPhone, setNewPhone] = useState<ContactPhone>({
    contactphoneid: 0,
    phonenumber: "",
    countrycode: "",
    phoneext: "",
    primaryind: false,
  });

  //Editing of Company
  const [contactcompany, setContactCompany] = useState<ContactCompany[]>([]);
  const [newCompany, setNewCompany] = useState<ContactCompany>({
    contactcompanyid: 0,
    companyname: "",
    entitytype: "",
    title: "",
    primaryind: false,
  });
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<number | null>(null);
  const [editedCompany, setEditedCompany] = useState<ContactCompany | null>(
    null,
  );

  //CRM
  const [contactcrm, setContactCRM] = useState<ContactCRMInfo[]>([]);
  //Not used atm but created for future implementation
  const [newcrm, setNewCRM] = useState<ContactCRMInfo>({
    crm_id: 0,
    owner: "",
    owner_cidn: 0,
    title: "",
    organization: "",
    pipeline: "",
    stage: "",
    last_stage_change: "",
    status: "",
    date_added: "",
    date_update: "",
  });

  //
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const {
    getContactByCIDN,
    getUsersByContactCIDN,
    getContactAddressByCIDN,
    getContactEmailByCIDN,
    getContactPhoneByCIDN,
    getContactCompanyByCIDN,
    getContactCRMByCIDN,
    deleteContactPhone,
    updateContactPhone,
    addContactPhone,
    deleteContactCompany,
    addContactCompany,
    updateContactCompany,
    deleteContactEmail,
    addContactEmail,
    updateContactEmail,
    deleteContactAddress,
    addContactAddress,
    updateContactAddress,
    getUsersDropdwn,
    addUserToContact,
    deleteContact,
    getStateProvince,
    getContactByEmail,
    updateContact,
    updateUserPrimaryInd,
    copyContactData,
  } = useAPI();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  //Load The Contact Information
  useEffect(() => {
    const fetchContact = async () => {
      try {
        if (!cidn) {
          return;
        }
        const response = await getContactByCIDN(Number(cidn));

        if (response?.data && response.data.length > 0) {
          setContact(response.data[0]);
        } else {
          throw new Error("Failed to fetch contact");
        }
      } catch (err) {
        console.error("Error fetching contact:", err);
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred fetching contact by cidn",
        );
      }
    };

    fetchContact();
  }, [cidn, getContactByCIDN]);

  const reloadContact = useCallback(async () => {
    try {
      if (!cidn) return;

      const response = await getContactByCIDN(Number(cidn));

      if (response?.data && response.data.length > 0) {
        setContact(response.data[0]); // 🔥 updates whole contact
      }
    } catch (err) {
      console.error("Error reloading contact:", err);
    }
  }, [cidn, getContactByCIDN]);

  useEffect(() => {
    reloadContact();
  }, [reloadContact]);

  //Populate Users associated to this Contact
  const fetchUsersByCIDN = useCallback(async () => {
    if (!cidn) return;
    const res = await getUsersByContactCIDN(Number(cidn));
    setUsers(res.data);
  }, [cidn, getUsersByContactCIDN]);

  useEffect(() => {
    fetchUsersByCIDN();
  }, [fetchUsersByCIDN]);

  //Populate Userlist for dropdown add option
  const fetchUserDropdown = useCallback(async () => {
    try {
      const res = await getUsersDropdwn();
      setUserDropDown(res.data); //
    } catch (err) {
      console.error("Failed to fetch users dropdown", err);
    }
  }, [getUsersDropdwn]);

  // Call it once when component mounts
  useEffect(() => {
    fetchUserDropdown();
  }, [fetchUserDropdown]);

  //Populate Address Grid
  const fetchContactAddressByCIDN = useCallback(async () => {
    if (!cidn) return;
    const res = await getContactAddressByCIDN(Number(cidn));
    setContactAddress(res.data);
  }, [cidn, getContactAddressByCIDN]);

  useEffect(() => {
    fetchContactAddressByCIDN();
  }, [fetchContactAddressByCIDN]);

  //Populate Email Grid
  const fetchContactEmailByCIDN = useCallback(async () => {
    if (!cidn) return;
    const res = await getContactEmailByCIDN(Number(cidn));
    setContactEmail(res.data);
  }, [cidn, getContactEmailByCIDN]);

  useEffect(() => {
    fetchContactEmailByCIDN();
  }, [fetchContactEmailByCIDN]);

  //Populate Phone Grid
  const fetchContactPhoneByCIDN = useCallback(async () => {
    if (!cidn) return;
    const res = await getContactPhoneByCIDN(Number(cidn));
    setContactPhone(res.data);
  }, [cidn, getContactPhoneByCIDN]);

  useEffect(() => {
    fetchContactPhoneByCIDN();
  }, [fetchContactPhoneByCIDN]);

  //Populate Company Grid
  const fetchContactCompanyByCIDN = useCallback(async () => {
    if (!cidn) return;
    const res = await getContactCompanyByCIDN(Number(cidn));
    setContactCompany(res.data);
  }, [cidn, getContactCompanyByCIDN]);

  useEffect(() => {
    fetchContactCompanyByCIDN();
  }, [fetchContactCompanyByCIDN]);

  //Handle Editing Contact
  const handleChangeContact = (field: string, value: any) => {
    setEditedContact((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleProfessionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    setEditedContact((prev: any) => {
      const currentProfessions = prev.profession || [];

      let updatedProfessions;

      if (checked) {
        // Add the value if checked
        updatedProfessions = [...currentProfessions, value];
      } else {
        // Remove the value if unchecked
        updatedProfessions = currentProfessions.filter(
          (prof: string) => prof !== value,
        );
      }

      return {
        ...prev,
        profession: updatedProfessions,
        // If "Other" gets unchecked, clear the text box
        profession_other:
          value === "Other" && !checked ? "" : prev.profession_other,
      };
    });
  };

  const handleInterestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;

    setEditedContact((prev: any) => {
      const current = prev.interests || [];

      const updated = checked
        ? [...current, value]
        : current.filter((i: string) => i !== value);

      return {
        ...prev,
        interests: updated,
        interests_other:
          value === "Other" && !checked ? "" : prev.interests_other,
      };
    });
  };

  const handleSaveContact = async (updatedContact: any) => {
    try {
      // Clean "Other" fields if not selected
      const cleanedContact = {
        ...updatedContact,
        streetaddress1: updatedContact.streetaddress, // rename for the function
        profession_other: updatedContact.profession?.includes("Other")
          ? updatedContact.profession_other
          : null,
        interests_other: updatedContact.interests?.includes("Other")
          ? updatedContact.interests_other
          : null,
      };

      await updateContact(Number(cidn), cleanedContact);

      setContact(updatedContact);

      setIsEditing(false);

      await fetchUsersByCIDN(); // refresh list
      await reloadContact(); // refresh main contact
      await fetchContactAddressByCIDN();
      await fetchContactCompanyByCIDN();
      await fetchContactEmailByCIDN();
      await fetchContactPhoneByCIDN();
    } catch (error) {
      console.error("Error saving contact:", error);
      alert("There was a problem saving changes.");
    }
  };

  //Populate CRM Grid
  const fetchContactCRMByCIDN = useCallback(async () => {
    if (!cidn) return;
    const res = await getContactCRMByCIDN(Number(cidn));
    setContactCRM(res.data);
  }, [cidn, getContactCRMByCIDN]);

  useEffect(() => {
    fetchContactCRMByCIDN();
  }, [fetchContactCRMByCIDN]);

  //Handle Adding New User to this Contact
  const handleEditUser = (u: Users) => {
    navigate(`/user-management/edit/${u.id}`, {
      state: { from: location.pathname },
    });
  };

  const handleEditUserPrimary = (user: Users) => {
    setEditingUserId(user.id);
    setEditedUser({ ...user }); // clone so we don’t edit original state directly
  };

  const handleUserChange = (field: keyof Users, value: string | boolean) => {
    if (!editedUser) return;
    setEditedUser({ ...editedUser, [field]: value });
  };

  const handleSaveUser = async () => {
    if (!editedUser) return;

    try {
      await updateUserPrimaryInd(
        Number(cidn),
        editedUser.id,
        editedUser.cidnprimaryind,
      );
      setEditingUserId(null);
      setEditedUser(null);
      await fetchUsersByCIDN(); // refresh list
      await reloadContact(); // refresh main contact
      await fetchContactAddressByCIDN();
      await fetchContactCompanyByCIDN();
      await fetchContactEmailByCIDN();
      await fetchContactPhoneByCIDN();
    } catch (err) {
      console.error("Failed to update user primary indicator", err);
    }
  };

  const handleNewUserChange = (field: keyof Users, value: any) => {
    setNewUser((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddUser = async () => {
    if (!newUser.id || !newUser.cidn) return;

    try {
      //Add this user to contact by updating user record to set to cidn
      await addUserToContact(Number(cidn), newUser.id);

      // Merge old cidn to new cidn
      await copyContactData(newUser.cidn, Number(cidn), newUser.id);

      //Now refresh all the lists
      await fetchUsersByCIDN(); // refresh the users table to include new one added
      await reloadContact(); // refresh main contact
      await fetchContactAddressByCIDN();
      await fetchContactCompanyByCIDN();
      await fetchContactEmailByCIDN();
      await fetchContactPhoneByCIDN();

      //After reloading contact, i want to do an auto save to copy over that new users data

      setIsAddingUser(false);
      setNewUser({
        id: "",
        firstname: "",
        lastname: "",
        email: "",
        cidn: 0,
        cidnprimaryind: false,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteUser = async (u: Users) => {
    if (
      window.confirm(
        `Are you sure you want to remove the user "${u.email}" from this contact? This action cannot be undone.`,
      )
    ) {
      try {
        //await removeUserFromContact(Number(cidn), u.id);

        // Refresh list
        await fetchUsersByCIDN();
        await reloadContact(); // refresh main contact
        await fetchContactAddressByCIDN();
        await fetchContactCompanyByCIDN();
        await fetchContactEmailByCIDN();
        await fetchContactPhoneByCIDN();
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to remove user from contact",
        );
      }
    }
  };

  //Handle Editing of Address and Adding of New Address
  const handleEditAddress = (address: ContactAddress) => {
    setEditingAddressId(address.contactaddressid);
    setEditedAddress({ ...address }); // clone so we don’t edit original state directly
  };

  const handleAddressChange = (
    field: keyof ContactAddress,
    value: string | boolean,
  ) => {
    if (!editedAddress) return;
    setEditedAddress({ ...editedAddress, [field]: value });
  };

  const handleSaveAddress = async () => {
    if (!editedAddress) return;

    try {
      await updateContactAddress(
        editedAddress.contactaddressid,
        editedAddress,
        Number(cidn),
      );
      setEditingAddressId(null);
      setEditedAddress(null);
      await fetchContactAddressByCIDN(); // refresh list
      await reloadContact(); // refresh main contact
    } catch (err) {
      console.error("Failed to update contact address", err);
    }
  };

  const handleNewAddressChange = (field: keyof ContactAddress, value: any) => {
    setNewAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddAddress = async () => {
    try {
      await addContactAddress(newAddress, Number(cidn));

      // Refresh Address list
      const res = await getContactAddressByCIDN(Number(cidn));
      setContactAddress(res.data);

      await reloadContact(); // refresh main contact

      // Reset form
      setIsAddingAddress(false);
      setNewAddress({
        contactaddressid: 0,
        streetaddress: "",
        streetaddress2: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        primaryind: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteAddress = async (
    contactaddressid: number,
    streetaddress?: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete the address "${streetaddress}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteContactAddress(contactaddressid);

        // Refresh list
        await fetchContactAddressByCIDN();
        await reloadContact(); // refresh main contact
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to delete contact address",
        );
      }
    }
  };

  //Handle Editing of Email and Adding of New Email Address
  const handleEditEmail = (email: ContactEmail) => {
    setEditingEmailId(email.contactemailid);
    setEditedEmail({ ...email }); // clone so we don’t edit original state directly
  };

  const handleEmailChange = (
    field: keyof ContactEmail,
    value: string | boolean,
  ) => {
    if (!editedEmail) return;
    setEditedEmail({ ...editedEmail, [field]: value });
  };

  const handleSaveEmail = async () => {
    if (!editedEmail) return;

    try {
      await updateContactEmail(
        editedEmail.contactemailid,
        editedEmail,
        Number(cidn),
      );
      setEditingEmailId(null);
      setEditedEmail(null);
      await fetchContactEmailByCIDN(); // refresh list
      await reloadContact(); // refresh main contact
    } catch (err) {
      console.error("Failed to update contact email", err);
    }
  };

  const handleNewEmailChange = (field: keyof ContactEmail, value: any) => {
    setNewEmail((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddEmail = async () => {
    try {
      // Clear previous error
      setEmailError("");

      if (newEmail.email) {
        // Check if email already exists
        const response = await getContactByEmail(newEmail.email);
        setContactExists(response?.data);
        const result = response?.data?.[0];

        // NO RECORD FOUND — safe to add
        if (!result || Object.keys(result).length === 0) {
          await addContactEmail(newEmail, Number(cidn));
        }
        // RECORD EXISTS but same cidn + inactive — allow add
        else if (result.cidn === Number(cidn) && !result.activeind) {
          await addContactEmail(newEmail, Number(cidn));
        }
        // EMAIL EXISTS — block & show error
        else {
          setEmailError("This email address already exists.");
          return;
        }
      }

      // Refresh Email list
      const res = await getContactEmailByCIDN(Number(cidn));
      setContactEmail(res.data);

      await reloadContact(); // refresh main contact

      // Reset form
      setIsAddingEmail(false);
      setNewEmail({
        contactemailid: 0,
        email: "",
        primaryind: false,
      });
    } catch (error) {
      console.error(error);
      setEmailError("Something went wrong while saving the email.");
    }
  };

  const handleDeleteEmail = async (contactemailid: number, email?: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete the email "${email}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteContactEmail(contactemailid);

        // Refresh list
        await fetchContactEmailByCIDN();
        await reloadContact(); // refresh main contact
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to delete contact email",
        );
      }
    }
  };

  //Handle Editing of Phone and Adding of New Phone Number
  const handleEditPhone = (phone: ContactPhone) => {
    setEditingPhoneId(phone.contactphoneid);

    // Default to US if missing
    const safeCountryCode =
      phone.countrycode && phone.countrycode.trim() !== ""
        ? phone.countrycode
        : "1";

    const fullNumber = phone.phonenumber
      ? `${safeCountryCode}${phone.phonenumber}`
      : "";

    setEditedPhone({
      ...phone,
      countrycode: safeCountryCode,
      phonenumber: phone.phonenumber || "",
    });
  };

  const handlePhoneChange = (
    field: keyof ContactPhone,
    value: string | boolean,
  ) => {
    if (!editedPhone) return;
    setEditedPhone({ ...editedPhone, [field]: value });
  };

  const handleSavePhone = async () => {
    if (!editedPhone) return;

    const payload = {
      ...editedPhone,
      countrycode: editedPhone.countrycode,
      phonenumber: editedPhone.phonenumber,
    };

    try {
      await updateContactPhone(
        editedPhone.contactphoneid,
        payload,
        Number(cidn),
      );

      setEditingPhoneId(null);
      setEditedPhone(null);
      await fetchContactPhoneByCIDN();
      await reloadContact();
    } catch (err) {
      console.error("Failed to update contact phone", err);
    }
  };

  const handleNewPhoneChange = (field: keyof ContactPhone, value: any) => {
    setNewPhone((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddPhone = async () => {
    try {
      await addContactPhone(newPhone, Number(cidn));

      // Refresh phone list
      const res = await getContactPhoneByCIDN(Number(cidn));
      setContactPhone(res.data);

      await reloadContact(); // refresh main contact

      // Reset form
      setIsAddingPhone(false);
      setNewPhone({
        contactphoneid: 0,
        phonenumber: "",
        countrycode: "",
        phoneext: "",
        primaryind: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeletePhone = async (
    contactphoneid: number,
    phonenumber?: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete the contact phonenumber "${phonenumber}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteContactPhone(contactphoneid);

        // Refresh list
        await fetchContactPhoneByCIDN();
        await reloadContact(); // refresh main contact
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to delete contact phone",
        );
      }
    }
  };

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // If the number is 11 digits and starts with 1 (US number)
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      const usNumber = cleaned.slice(1);
      return `(${usNumber.slice(0, 3)}) ${usNumber.slice(
        3,
        6,
      )}-${usNumber.slice(6)}`;
    }

    // If the number is 10 digits (US number without country code)
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    }

    // For international numbers, format as +XX XXX XXX XXXX
    // This handles numbers with country codes of varying lengths
    const countryCode = cleaned.slice(0, cleaned.length - 10);
    const nationalNumber = cleaned.slice(cleaned.length - 10);

    return `+${countryCode} ${nationalNumber.slice(
      0,
      3,
    )} ${nationalNumber.slice(3, 6)} ${nationalNumber.slice(6)}`;
  };

  //Handle Editing of Company and Adding of New Company
  const handleEditCompany = (company: ContactCompany) => {
    setEditingCompanyId(company.contactcompanyid);
    setEditedCompany({ ...company }); // clone so we don’t edit original state directly
  };

  const handleCompanyChange = (
    field: keyof ContactCompany,
    value: string | boolean,
  ) => {
    if (!editedCompany) return;
    setEditedCompany({ ...editedCompany, [field]: value });
  };

  const handleSaveCompany = async () => {
    if (!editedCompany) return;

    try {
      await updateContactCompany(
        editedCompany.contactcompanyid,
        editedCompany,
        Number(cidn),
      );
      setEditingCompanyId(null);
      setEditedCompany(null);
      await fetchContactCompanyByCIDN(); // refresh list
      await reloadContact(); // refresh main contact
    } catch (err) {
      console.error("Failed to update contact company", err);
    }
  };

  const handleNewCompanyChange = (field: keyof ContactCompany, value: any) => {
    setNewCompany((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddCompany = async () => {
    try {
      await addContactCompany(newCompany, Number(cidn));

      // Refresh Company list
      const res = await getContactCompanyByCIDN(Number(cidn));
      setContactCompany(res.data);

      await reloadContact(); // refresh main contact

      // Reset form
      setIsAddingCompany(false);
      setNewCompany({
        contactcompanyid: 0,
        companyname: "",
        entitytype: "",
        title: "",
        primaryind: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteCompany = async (
    contactcompanyid: number,
    companyname?: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete the company "${companyname}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteContactCompany(contactcompanyid);

        // Refresh list
        await fetchContactCompanyByCIDN();
        await reloadContact(); // refresh main contact
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "Failed to delete contact company",
        );
      }
    }
  };

  const handleReturn = () => {
    navigate("/contact-management");
  };

  //Handle Editing Contact
  const handleEditContact = (c: Contact) => {
    //Open up edit options
  };

  const handleDeleteContact = async (
    contactFN: string,
    contactLN: string,
    email: string,
  ) => {
    if (
      window.confirm(
        `Are you sure you want to delete the contact "${contactFN} ${contactLN}"? This action cannot be undone.`,
      )
    ) {
      try {
        const addedbyID = user?.id ?? "admin";

        await deleteContact(Number(cidn), addedbyID, email);

        // // Redirect out of this page after delete
        navigate("/contact-management");
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to delete contact",
        );
      }
    }
  };

  return (
    <div className="generalPage">
      <Header2 />
      <div className="contactmanagement-container">
        <div className="pageContainer">
          <div className="button-container">
            <button className="back-button" onClick={() => navigate("/")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </button>
          </div>
          <div className="contactmanagement-header">
            <div className="contactmanagement-actions">
              <h1 className="contactmanagement-title">
                Contact Management View
              </h1>

              <button onClick={handleReturn} className="add-user-button">
                Return Contact Management
              </button>
            </div>
          </div>
          <div className="contactsview-table-container">
            <table className="contactsdetail-table">
              <colgroup>
                <col style={{ width: "30%" }} /> {/* Main text column */}
                <col style={{ width: "70%" }} /> {/* second column */}
              </colgroup>
              <thead>
                <th colSpan={2} className="contactsdetail-table-section_header">
                  <div className="header-with-button">
                    <span>Contact Details</span>
                  </div>
                </th>
              </thead>
              <tbody>
                {(!isEditing && contact?.firstname) || isEditing ? (
                  <tr>
                    <th>First Name</th>
                    <td className="input-form-group">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedContact?.firstname || ""}
                          placeholder="Enter First Name"
                          onChange={(e) =>
                            handleChangeContact("firstname", e.target.value)
                          }
                        />
                      ) : (
                        contact?.firstname
                      )}
                    </td>
                  </tr>
                ) : null}
                {(!isEditing && contact?.middlename) || isEditing ? (
                  <tr>
                    <th>Middle Name</th>
                    <td className="input-form-group">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedContact?.middlename || ""}
                          placeholder="Enter Middle Name"
                          onChange={(e) =>
                            handleChangeContact("middlename", e.target.value)
                          }
                        />
                      ) : (
                        contact?.middlename
                      )}
                    </td>
                  </tr>
                ) : null}
                {(!isEditing && contact?.lastname) || isEditing ? (
                  <tr>
                    <th>Last Name</th>
                    <td className="input-form-group">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedContact?.lastname || ""}
                          placeholder="Enter Last Name"
                          onChange={(e) =>
                            handleChangeContact("lastname", e.target.value)
                          }
                        />
                      ) : (
                        contact?.lastname
                      )}
                    </td>
                  </tr>
                ) : null}
                {(!isEditing && contact?.profession) || isEditing ? (
                  <tr>
                    <th>Professions</th>
                    <td className="input-form-group">
                      {isEditing ? (
                        <>
                          <div className="checkbox-group">
                            <div>
                              {PROFESSION_OPTIONS.map((option) => (
                                <div key={option} className="checkbox-item">
                                  <label htmlFor={`profession-${option}`}>
                                    <input
                                      type="checkbox"
                                      id={`profession-${option}`}
                                      name="profession"
                                      value={option}
                                      checked={
                                        editedContact?.profession?.includes(
                                          option,
                                        ) || false
                                      }
                                      onChange={handleProfessionChange}
                                    />{" "}
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                          {editedContact?.profession?.includes("Other") && (
                            <div style={{ marginTop: "6px" }}>
                              <input
                                type="text"
                                placeholder="Other profession"
                                value={editedContact?.profession_other || ""}
                                onChange={(e) =>
                                  handleChangeContact(
                                    "profession_other",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="contactsdetail-wrap-text">
                            {contact?.profession?.join(", ")}
                            {contact?.profession_other &&
                              contact?.profession?.includes("Other") && (
                                <span> ({contact.profession_other})</span>
                              )}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ) : null}
                {(!isEditing && contact?.interests) || isEditing ? (
                  <tr>
                    <th>Interests</th>
                    <td className="input-form-group">
                      {isEditing ? (
                        <>
                          <div className="checkbox-group">
                            <div>
                              {INTEREST_OPTIONS.map((option) => (
                                <div key={option} className="checkbox-item">
                                  <label htmlFor={`interests-${option}`}>
                                    <input
                                      type="checkbox"
                                      id={`interests-${option}`}
                                      name="interests"
                                      value={option}
                                      checked={
                                        editedContact?.interests?.includes(
                                          option,
                                        ) || false
                                      }
                                      onChange={handleInterestChange}
                                    />{" "}
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                          {editedContact?.interests?.includes("Other") && (
                            <div style={{ marginTop: "6px" }}>
                              <input
                                type="text"
                                placeholder="Other interest"
                                value={editedContact?.interests_other || ""}
                                onChange={(e) =>
                                  handleChangeContact(
                                    "interests_other",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="contactsdetail-wrap-text">
                            {contact?.interests?.join(", ")}
                            {contact?.interests_other &&
                              contact?.interests?.includes("Other") && (
                                <span> ({contact.interests_other})</span>
                              )}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ) : null}
                <tr>
                  <th>DataSource</th>
                  <td>{contact?.datasource}</td>
                </tr>
                <tr>
                  <td colSpan={2}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => {
                            handleSaveContact(editedContact);
                            setIsEditing(false);
                          }}
                          className="action-button edit-button"
                        >
                          Save
                        </button>

                        <button
                          onClick={() => {
                            setEditedContact(contact);
                            setIsEditing(false);
                          }}
                          className="action-button delete-button"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setIsEditing(true)}
                          className="action-button edit-button"
                        >
                          Edit
                        </button>

                        {contact && !contact.hasuser && (
                          <button
                            onClick={() =>
                              handleDeleteContact(
                                contact.firstname,
                                contact.lastname,
                                contact.email,
                              )
                            }
                            className="action-button delete-button"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            <table className="contactsuserview-table">
              <colgroup>
                <col style={{ width: "70px" }} /> {/* Main text column */}
                <col style={{ width: "70px" }} /> {/* Secondary 1 */}
                <col style={{ width: "180px" }} /> {/* Secondary 2 */}
                <col style={{ width: "50px" }} /> {/* Secondary 3 */}
                <col style={{ width: "130px" }} /> {/* Action */}
              </colgroup>
              <thead>
                <th
                  colSpan={5}
                  className="contactsuserview-table-section_header"
                >
                  <div className="header-with-button">
                    <span>Users Associated to Contact</span>
                    {!isAddingUser && (
                      <button
                        onClick={() => setIsAddingUser(true)}
                        className="action-button add-button"
                      >
                        + Add New User
                      </button>
                    )}
                  </div>
                </th>
              </thead>
              <tbody>
                {!isAddingUser && (
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Primary</th>
                    <th>Action</th>
                  </tr>
                )}

                {isAddingUser && (
                  <tr>
                    <td colSpan={4} className="input-form-group">
                      <select
                        id="id"
                        name="id"
                        onChange={(e) => {
                          const selected = userdropdown.find(
                            (u) => u.id === e.target.value,
                          );

                          if (!selected) return;

                          setNewUser((prev) => ({
                            ...prev,
                            id: selected.id,
                            cidn: selected.cidn,
                          }));
                        }}
                      >
                        <option value="">-- Select Existing User --</option>
                        {userdropdown.map((ul) => (
                          <option key={ul.id} value={ul.id}>
                            {" "}
                            {/* ✅ use ul.id */}
                            {ul.firstlastemail}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        onClick={handleAddUser}
                        className="action-button edit-button"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setIsAddingUser(false)}
                        className="action-button delete-button"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}

                {users.length > 0 ? (
                  users.map((u) => {
                    const isEditing = editingUserId === u.id;

                    return (
                      <tr key={u.id}>
                        <td>{u.firstname}</td>
                        <td>{u.lastname}</td>
                        <td>{u.email}</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={editedUser?.cidnprimaryind || false}
                              onChange={(e) =>
                                handleUserChange(
                                  "cidnprimaryind",
                                  e.target.checked,
                                )
                              }
                            />
                          ) : u.cidnprimaryind ? (
                            "True"
                          ) : (
                            "False"
                          )}
                        </td>

                        <td>
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveUser}
                                className="action-button edit-button"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingUserId(null);
                                  setEditedUser(null);
                                }}
                                className="action-button delete-button"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              {users.length > 1 && (
                                <button
                                  onClick={() => handleEditUserPrimary(u)}
                                  className="action-button primary-button"
                                >
                                  Edit Primary
                                </button>
                              )}
                              <button
                                onClick={() => handleEditUser(u)}
                                className="action-button edit-button"
                              >
                                Edit
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="not-active-text">
                      No users linked to this contact
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="contactsview-table-container">
            <table className="contactsview-table">
              <colgroup>
                <col style={{ width: "120px" }} /> {/* Main text column */}
                <col style={{ width: "100px" }} /> {/* Secondary 1 */}
                <col style={{ width: "100px" }} /> {/* Secondary 2 */}
                <col style={{ width: "80px" }} /> {/* Secondary 3 */}
                <col style={{ width: "100px" }} /> {/* Secondary 4 */}
                <col /> {/* Primary */}
                <col style={{ width: "100px" }} /> {/* Action */}
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={7} className="contactsview-table-section_header">
                    <div className="header-with-button">
                      <span>Address Details</span>
                      {!isAddingAddress && (
                        <button
                          onClick={() => setIsAddingAddress(true)}
                          className="action-button add-button"
                        >
                          + Add New Address
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Address</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Zip</th>
                  <th>Country</th>
                  <th>Primary</th>
                  <th>Action</th>
                </tr>
                {isAddingAddress && (
                  <tr>
                    <td className="input-form-group">
                      <input
                        value={newAddress.streetaddress}
                        className="textbox-large-input"
                        placeholder="Enter Address"
                        onChange={(e) =>
                          handleNewAddressChange(
                            "streetaddress",
                            e.target.value,
                          )
                        }
                      />
                      <input
                        value={newAddress.streetaddress2}
                        className="textbox-large-input"
                        placeholder="Enter Address2"
                        onChange={(e) =>
                          handleNewAddressChange(
                            "streetaddress2",
                            e.target.value,
                          )
                        }
                      />
                    </td>
                    <td className="input-form-group">
                      <input
                        value={newAddress.city}
                        placeholder="Enter City"
                        onChange={(e) =>
                          handleNewAddressChange("city", e.target.value)
                        }
                      />
                    </td>
                    <td className="input-form-group">
                      <select
                        id="state"
                        name="state"
                        value={newAddress.state}
                        onChange={(e) =>
                          handleNewAddressChange("state", e.target.value)
                        }
                      >
                        <option value="">-- Select State --</option>
                        {states.map((s) => (
                          <option
                            key={s.stateprovinceabbrv}
                            value={s.stateprovinceabbrv}
                          >
                            {s.stateprovincename}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="input-form-group">
                      <input
                        value={newAddress.zip}
                        className="textbox-small-input"
                        placeholder="Enter Zip"
                        onChange={(e) =>
                          handleNewAddressChange("zip", e.target.value)
                        }
                      />
                    </td>
                    <td className="input-form-group">
                      <input
                        value={newAddress.country}
                        placeholder="Enter Country"
                        onChange={(e) =>
                          handleNewAddressChange("country", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={newAddress.primaryind}
                        onChange={(e) =>
                          handleNewAddressChange("primaryind", e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <button
                        onClick={handleAddAddress}
                        className="action-button edit-button"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setIsAddingAddress(false)}
                        className="action-button delete-button"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}

                {contactaddress.length > 0 ? (
                  contactaddress.map((ca) => {
                    const isEditing = editingAddressId === ca.contactaddressid;

                    return (
                      <tr key={ca.contactaddressid}>
                        <td className="input-form-group">
                          {isEditing ? (
                            <>
                              <input
                                value={editedAddress?.streetaddress || ""}
                                className="textbox-large-input"
                                placeholder="Enter Address"
                                onChange={(e) =>
                                  handleAddressChange(
                                    "streetaddress",
                                    e.target.value,
                                  )
                                }
                              />

                              <input
                                value={editedAddress?.streetaddress2 || ""}
                                className="textbox-large-input"
                                placeholder="Enter Address2"
                                onChange={(e) =>
                                  handleAddressChange(
                                    "streetaddress2",
                                    e.target.value,
                                  )
                                }
                              />
                            </>
                          ) : (
                            <>
                              {ca.streetaddress}
                              {ca.streetaddress2 && (
                                <>
                                  <br />
                                  {ca.streetaddress2}
                                </>
                              )}
                            </>
                          )}
                        </td>
                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedAddress?.city || ""}
                              placeholder="Enter City"
                              onChange={(e) =>
                                handleAddressChange("city", e.target.value)
                              }
                            />
                          ) : (
                            ca.city
                          )}
                        </td>
                        <td className="input-form-group">
                          {isEditing ? (
                            <select
                              id="state"
                              name="state"
                              value={editedAddress?.state || ""}
                              onChange={(e) =>
                                handleAddressChange("state", e.target.value)
                              }
                            >
                              <option value="">-- Select State --</option>
                              {states.map((s) => (
                                <option
                                  key={s.stateprovinceabbrv}
                                  value={s.stateprovinceabbrv}
                                >
                                  {s.stateprovincename}
                                </option>
                              ))}
                            </select>
                          ) : (
                            ca.state
                          )}
                        </td>
                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedAddress?.zip || ""}
                              placeholder="Enter Zip"
                              className="textbox-small-input"
                              onChange={(e) =>
                                handleAddressChange("zip", e.target.value)
                              }
                            />
                          ) : (
                            ca.zip
                          )}
                        </td>
                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedAddress?.country || ""}
                              placeholder="Enter Country"
                              onChange={(e) =>
                                handleAddressChange("country", e.target.value)
                              }
                            />
                          ) : (
                            ca.country
                          )}
                        </td>
                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={editedAddress?.primaryind || false}
                              onChange={(e) =>
                                handleAddressChange(
                                  "primaryind",
                                  e.target.checked,
                                )
                              }
                            />
                          ) : ca.primaryind ? (
                            "True"
                          ) : (
                            "False"
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveAddress}
                                className="action-button edit-button"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingAddressId(null);
                                  setEditedAddress(null);
                                }}
                                className="action-button delete-button"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditAddress(ca)}
                                className="action-button edit-button"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteAddress(
                                    ca.contactaddressid,
                                    ca.streetaddress,
                                  )
                                }
                                className="action-button delete-button"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="not-active-text">
                      No addresses linked to this contact
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="contactsview-table-container">
            <table className="contactsview-table">
              <colgroup>
                <col style={{ width: "120px" }} /> {/* Main text column */}
                <col style={{ width: "100px" }} /> {/* Secondary 1 */}
                <col style={{ width: "100px" }} /> {/* Secondary 2 */}
                <col style={{ width: "80px" }} /> {/* Secondary 3 */}
                <col style={{ width: "100px" }} /> {/* Secondary 4 */}
                <col /> {/* Primary */}
                <col style={{ width: "100px" }} /> {/* Action */}
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={7} className="contactsview-table-section_header">
                    <div className="header-with-button">
                      <span>Email Details</span>
                      {!isAddingEmail && (
                        <button
                          onClick={() => setIsAddingEmail(true)}
                          className="action-button add-button"
                        >
                          + Add New Email
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Email</th>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                  <th>Primary</th>
                  <th>Action</th>
                </tr>
                {isAddingEmail && (
                  <tr>
                    <td className="input-form-group">
                      <input
                        value={newEmail.email}
                        className="textbox-large-input"
                        placeholder="Enter Email"
                        onChange={(e) =>
                          handleNewEmailChange("email", e.target.value)
                        }
                      />
                      {emailError && (
                        <div className="error-message-contact">
                          {emailError}
                        </div>
                      )}
                    </td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={newEmail.primaryind}
                        onChange={(e) =>
                          handleNewEmailChange("primaryind", e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <button
                        onClick={handleAddEmail}
                        className="action-button edit-button"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setIsAddingEmail(false)}
                        className="action-button delete-button"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}
                {contactemail.length > 0 ? (
                  contactemail.map((ce) => {
                    const isEditing = editingEmailId === ce.contactemailid;

                    return (
                      <tr key={ce.contactemailid}>
                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedEmail?.email || ""}
                              className="textbox-large-input"
                              placeholder="Enter Email"
                              onChange={(e) =>
                                handleEmailChange("email", e.target.value)
                              }
                            />
                          ) : (
                            ce.email
                          )}
                        </td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={editedEmail?.primaryind || false}
                              onChange={(e) =>
                                handleEmailChange(
                                  "primaryind",
                                  e.target.checked,
                                )
                              }
                            />
                          ) : ce.primaryind ? (
                            "True"
                          ) : (
                            "False"
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveEmail}
                                className="action-button edit-button"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingEmailId(null);
                                  setEditedEmail(null);
                                }}
                                className="action-button delete-button"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditEmail(ce)}
                                className="action-button edit-button"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteEmail(ce.contactemailid, ce.email)
                                }
                                className="action-button delete-button"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="not-active-text">
                      No email addresses linked to this contact
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="contactsview-table-container">
            <table className="contactsview-table">
              <colgroup>
                <col style={{ width: "100px" }} /> {/* Main text column */}
                <col style={{ width: "100px" }} /> {/* Secondary 1 */}
                <col style={{ width: "100px" }} /> {/* Secondary 2 */}
                <col style={{ width: "80px" }} /> {/* Secondary 3 */}
                <col style={{ width: "100px" }} /> {/* Secondary 4 */}
                <col /> {/* Primary */}
                <col style={{ width: "100px" }} /> {/* Action */}
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={7} className="contactsview-table-section_header">
                    <div className="header-with-button">
                      <span>Phone Details</span>
                      {!isAddingPhone && (
                        <button
                          onClick={() => setIsAddingPhone(true)}
                          className="action-button add-button"
                        >
                          + Add New Phone
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Phone Number</th>
                  <th>Phone Ext</th>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                  <th>Primary</th>
                  <th>Action</th>
                </tr>
                {isAddingPhone && (
                  <tr>
                    <td className="input-form-group">
                      <PhoneInput
                        country={"us"}
                        value={
                          newPhone.countrycode && newPhone.phonenumber
                            ? `${newPhone.countrycode}${newPhone.phonenumber}`
                            : ""
                        }
                        onChange={(value: string, data: any) => {
                          const dialCode = data?.dialCode || "";
                          const nationalNumber = value.slice(dialCode.length);

                          setNewPhone((prev) => ({
                            ...prev,
                            countrycode: dialCode,
                            phonenumber: nationalNumber,
                          }));
                        }}
                        inputClass="phone-input"
                        containerClass="phone-input-container"
                        placeholder="Enter phone number"
                      />
                    </td>
                    <td className="input-form-group">
                      <input
                        value={newPhone.phoneext}
                        placeholder="Enter Ext"
                        className="textbox-small-input"
                        onChange={(e) =>
                          handleNewPhoneChange("phoneext", e.target.value)
                        }
                      />
                    </td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={newPhone.primaryind}
                        onChange={(e) =>
                          handleNewPhoneChange("primaryind", e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <button
                        onClick={handleAddPhone}
                        className="action-button edit-button"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setIsAddingPhone(false)}
                        className="action-button delete-button"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}
                {contactphone.length > 0 ? (
                  contactphone.map((cp) => {
                    const isEditing = editingPhoneId === cp.contactphoneid;

                    return (
                      <tr key={cp.contactphoneid}>
                        <td className="input-form-group">
                          {isEditing ? (
                            <PhoneInput
                              value={`${editedPhone?.countrycode || ""}${editedPhone?.phonenumber || ""}`}
                              onChange={(value: string, data: any) => {
                                const dialCode = data?.dialCode || "";

                                setEditedPhone((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        countrycode: dialCode,
                                        phonenumber: value.slice(
                                          dialCode.length,
                                        ),
                                      }
                                    : null,
                                );
                              }}
                              inputClass="phone-input"
                              containerClass="phone-input-container"
                              disableCountryGuess
                            />
                          ) : (
                            formatPhoneNumber(
                              `${cp.countrycode || ""}${cp.phonenumber || ""}`,
                            )
                          )}
                        </td>

                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedPhone?.phoneext || ""}
                              placeholder="Enter Ext"
                              className="textbox-small-input"
                              onChange={(e) =>
                                handlePhoneChange("phoneext", e.target.value)
                              }
                            />
                          ) : (
                            cp.phoneext
                          )}
                        </td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={editedPhone?.primaryind || false}
                              onChange={(e) =>
                                handlePhoneChange(
                                  "primaryind",
                                  e.target.checked,
                                )
                              }
                            />
                          ) : cp.primaryind ? (
                            "True"
                          ) : (
                            "False"
                          )}
                        </td>

                        <td>
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSavePhone}
                                className="action-button edit-button"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingPhoneId(null);
                                  setEditedPhone(null);
                                }}
                                className="action-button delete-button"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditPhone(cp)}
                                className="action-button edit-button"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  handleDeletePhone(
                                    cp.contactphoneid,
                                    cp.phonenumber,
                                  )
                                }
                                className="action-button delete-button"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="not-active-text">
                      No phone numbers linked to this contact
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="contactsview-table-container">
            <table className="contactsview-table">
              <colgroup>
                <col style={{ width: "120px" }} /> {/* Main text column */}
                <col style={{ width: "100px" }} /> {/* Secondary 1 */}
                <col style={{ width: "100px" }} /> {/* Secondary 2 */}
                <col style={{ width: "80px" }} /> {/* Secondary 3 */}
                <col style={{ width: "100px" }} /> {/* Secondary 4 */}
                <col /> {/* Primary */}
                <col style={{ width: "100px" }} /> {/* Action */}
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={7} className="contactsview-table-section_header">
                    <div className="header-with-button">
                      <span>Company Details</span>
                      {!isAddingCompany && (
                        <button
                          onClick={() => setIsAddingCompany(true)}
                          className="action-button add-button"
                        >
                          + Add New Company
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Company Name</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>&nbsp;</th>
                  <th>&nbsp;</th>
                  <th>Primary</th>
                  <th>Action</th>
                </tr>
                {isAddingCompany && (
                  <tr>
                    <td className="input-form-group">
                      <input
                        value={newCompany.companyname}
                        className="textbox-large-input"
                        placeholder="Enter Company Name"
                        onChange={(e) =>
                          handleNewCompanyChange("companyname", e.target.value)
                        }
                      />
                    </td>
                    <td className="input-form-group">
                      <input
                        value={newCompany.entitytype}
                        placeholder="Enter Type"
                        onChange={(e) =>
                          handleNewCompanyChange("entitytype", e.target.value)
                        }
                      />
                    </td>
                    <td className="input-form-group">
                      <input
                        value={newCompany.title}
                        placeholder="Enter Title"
                        onChange={(e) =>
                          handleNewCompanyChange("title", e.target.value)
                        }
                      />
                    </td>
                    <td>&nbsp;</td>
                    <td>&nbsp;</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={newCompany.primaryind}
                        onChange={(e) =>
                          handleNewCompanyChange("primaryind", e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <button
                        onClick={handleAddCompany}
                        className="action-button edit-button"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => setIsAddingCompany(false)}
                        className="action-button delete-button"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                )}

                {contactcompany.length > 0 ? (
                  contactcompany.map((cc) => {
                    const isEditing = editingCompanyId === cc.contactcompanyid;

                    return (
                      <tr key={cc.contactcompanyid}>
                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedCompany?.companyname || ""}
                              className="textbox-large-input"
                              placeholder="Enter Company Name"
                              onChange={(e) =>
                                handleCompanyChange(
                                  "companyname",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            cc.companyname
                          )}
                        </td>

                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedCompany?.entitytype || ""}
                              placeholder="Enter Type"
                              onChange={(e) =>
                                handleCompanyChange(
                                  "entitytype",
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            cc.entitytype
                          )}
                        </td>

                        <td className="input-form-group">
                          {isEditing ? (
                            <input
                              value={editedCompany?.title || ""}
                              placeholder="Enter Title"
                              onChange={(e) =>
                                handleCompanyChange("title", e.target.value)
                              }
                            />
                          ) : (
                            cc.title
                          )}
                        </td>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="checkbox"
                              checked={editedCompany?.primaryind || false}
                              onChange={(e) =>
                                handleCompanyChange(
                                  "primaryind",
                                  e.target.checked,
                                )
                              }
                            />
                          ) : cc.primaryind ? (
                            "True"
                          ) : (
                            "False"
                          )}
                        </td>

                        <td>
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveCompany}
                                className="action-button edit-button"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingCompanyId(null);
                                  setEditedCompany(null);
                                }}
                                className="action-button delete-button"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditCompany(cc)}
                                className="action-button edit-button"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteCompany(
                                    cc.contactcompanyid,
                                    cc.companyname,
                                  )
                                }
                                className="action-button delete-button"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="not-active-text">
                      No companies linked to this contact
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="contactsview-table-container">
            <table className="contactsview-table">
              <colgroup>
                <col style={{ width: "80px" }} /> {/* Main text column */}
                <col style={{ width: "100px" }} /> {/* Secondary 1 */}
                <col style={{ width: "100px" }} /> {/* Secondary 2 */}
                <col style={{ width: "80px" }} /> {/* Secondary 3 */}
                <col style={{ width: "100px" }} /> {/* Secondary 4 */}
                <col style={{ width: "100px" }} /> {/* Primary */}
                <col style={{ width: "100px" }} /> {/* Action */}
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={7} className="contactsview-table-section_header">
                    <div className="header-with-button">
                      <span>CRM Details</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Owner</th>
                  <th>Title</th>
                  <th>Organization</th>
                  <th>Pipeline</th>
                  <th>Stage</th>
                  <th>Stage Updated</th>
                  <th>Status</th>
                </tr>
                {contactcrm.length > 0 ? (
                  contactcrm.map((cc) => {
                    return (
                      <tr key={cc.crm_id}>
                        <td>{cc.owner}</td>
                        <td>{cc.title}</td>
                        <td>{cc.organization}</td>
                        <td>{cc.pipeline}</td>
                        <td>{cc.stage}</td>
                        <td>{cc.last_stage_change}</td>
                        <td>{cc.status}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="not-active-text">
                      No CRM data linked to this contact
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
